import React, { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';

interface ConstructionRevealProps {
    isActive: boolean;
    onCovered: () => void;
    onComplete: () => void;
}

// Sound effect URLs (royalty-free industrial sounds)
const METAL_SOUNDS = {
    clank1: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==', // Placeholder
};

const ConstructionReveal: React.FC<ConstructionRevealProps> = ({ isActive, onCovered, onComplete }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const blocksRef = useRef<HTMLDivElement[]>([]);
    const audioContextRef = useRef<AudioContext | null>(null);

    // Metal clanking sound generator
    const playMetalSound = useCallback(() => {
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new ((window.AudioContext || (window as any).webkitAudioContext) as any)();
            }

            const ctx = audioContextRef.current!;
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            // Use 'triangle' for a softer, weightier sound (less harsh than square)
            oscillator.type = 'triangle';
            // Lower frequency for a "heavy object" thud
            oscillator.frequency.setValueAtTime(80, ctx.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.1);

            // Smoother volume envelope
            gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + 0.15);
        } catch (e) {
            // Audio not supported, continue silently
        }
    }, []);

    // Screen shake effect
    const shakeScreen = useCallback(() => {
        if (!containerRef.current) return;

        gsap.to(containerRef.current, {
            x: 'random(-1, 1)', // Reduced shake for smoothness
            y: 'random(-1, 1)',
            duration: 0.05,
            repeat: 1,
            yoyo: true,
            onComplete: () => {
                gsap.set(containerRef.current, { x: 0, y: 0 });
            }
        });
    }, []);

    useEffect(() => {
        if (!isActive || !containerRef.current) return;

        const container = containerRef.current;
        const blocks = blocksRef.current;
        const tl = gsap.timeline({
            onComplete: () => {
                // Final assembly complete - Switch Content Here
                onCovered();

                gsap.to(container, {
                    opacity: 0,
                    duration: 0.8, // Slower, smoother fade out
                    ease: 'power2.inOut',
                    delay: 0.1,
                    onComplete
                });
            }
        });

        // Initial state
        gsap.set(container, { opacity: 1 });
        gsap.set(blocks, {
            y: -window.innerHeight - 200,
            opacity: 1,
            rotation: 'random(-20, 20)'
        });

        // Block drop sequence with stagger
        blocks.forEach((block, index) => {
            const delay = index * 0.06;
            const targetY = block.dataset.targetY || 0;
            const targetX = block.dataset.targetX || 0;

            tl.to(block, {
                y: targetY,
                x: targetX,
                rotation: 0,
                duration: 0.6, // Slower for weight
                ease: 'power3.out', // Smoother than bounce
                onComplete: () => {
                    playMetalSound();
                    shakeScreen();
                }
            }, delay);
        });

        // Assembly animation - blocks slide into final positions
        tl.to(blocks, {
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.5,
            ease: 'expo.out', // Very smooth snap
            stagger: 0.03
        }, '+=0.1');

        // Frame assembly glow
        tl.to('.construction-frame', {
            boxShadow: '0 0 60px rgba(163, 255, 0, 0.6), inset 0 0 30px rgba(163, 255, 0, 0.2)',
            duration: 0.4
        }, '-=0.2');

        return () => {
            tl.kill();
        };
    }, [isActive, onCovered, onComplete, playMetalSound, shakeScreen]);

    if (!isActive) return null;

    // Generate construction blocks
    const blockPositions = [
        // Top frame pieces
        { id: 1, style: { width: '25%', height: '60px', left: '0%', top: '0%' }, targetY: 0, targetX: 0 },
        { id: 2, style: { width: '25%', height: '60px', left: '25%', top: '0%' }, targetY: 0, targetX: 0 },
        { id: 3, style: { width: '25%', height: '60px', left: '50%', top: '0%' }, targetY: 0, targetX: 0 },
        { id: 4, style: { width: '25%', height: '60px', left: '75%', top: '0%' }, targetY: 0, targetX: 0 },

        // Left frame pieces
        { id: 5, style: { width: '60px', height: '25%', left: '0%', top: '60px' }, targetY: 0, targetX: 0 },
        { id: 6, style: { width: '60px', height: '25%', left: '0%', top: 'calc(25% + 60px)' }, targetY: 0, targetX: 0 },

        // Right frame pieces
        { id: 7, style: { width: '60px', height: '25%', right: '0%', top: '60px' }, targetY: 0, targetX: 0 },
        { id: 8, style: { width: '60px', height: '25%', right: '0%', top: 'calc(25% + 60px)' }, targetY: 0, targetX: 0 },

        // Bottom frame pieces
        { id: 9, style: { width: '33%', height: '60px', left: '0%', bottom: '0%' }, targetY: 0, targetX: 0 },
        { id: 10, style: { width: '34%', height: '60px', left: '33%', bottom: '0%' }, targetY: 0, targetX: 0 },
        { id: 11, style: { width: '33%', height: '60px', right: '0%', bottom: '0%' }, targetY: 0, targetX: 0 },

        // Center filling blocks
        { id: 12, style: { width: '40%', height: '100px', left: '30%', top: '30%' }, targetY: 0, targetX: 0 },
        { id: 13, style: { width: '30%', height: '80px', left: '35%', top: '50%' }, targetY: 0, targetX: 0 },
    ];

    return (
        <div
            ref={containerRef}
            className="construction-reveal"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: '#000',
                overflow: 'hidden',
                opacity: 0
            }}
        >
            {/* Sparks container */}
            <div className="sparks-container" />

            {/* Construction blocks */}
            {blockPositions.map((block, index) => (
                <div
                    key={block.id}
                    ref={el => { if (el) blocksRef.current[index] = el; }}
                    data-target-y={block.targetY}
                    data-target-x={block.targetX}
                    className="construction-block"
                    style={{
                        position: 'absolute',
                        ...block.style,
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                        border: '2px solid #a3ff00',
                        boxShadow: '0 0 20px rgba(163, 255, 0, 0.3), inset 0 0 10px rgba(163, 255, 0, 0.1)',
                        borderRadius: '4px',
                    }}
                >
                    {/* Industrial texture */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(163, 255, 0, 0.05) 10px,
                rgba(163, 255, 0, 0.05) 20px
              )`,
                            borderRadius: '2px'
                        }}
                    />

                    {/* Rivet/bolt decorations */}
                    <div className="block-rivets">
                        <span className="rivet" style={{ top: 8, left: 8 }} />
                        <span className="rivet" style={{ top: 8, right: 8 }} />
                        <span className="rivet" style={{ bottom: 8, left: 8 }} />
                        <span className="rivet" style={{ bottom: 8, right: 8 }} />
                    </div>
                </div>
            ))}

            {/* Final assembled frame glow */}
            <div
                className="construction-frame"
                style={{
                    position: 'absolute',
                    inset: '40px',
                    border: '3px solid transparent',
                    borderRadius: '12px',
                    pointerEvents: 'none'
                }}
            />

            <style>{`
        .rivet {
          position: absolute;
          width: 6px;
          height: 6px;
          background: #a3ff00;
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(163, 255, 0, 0.8);
        }
        
        .construction-block {
          will-change: transform;
        }
        
        .sparks-container {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }
      `}</style>
        </div>
    );
};

export default ConstructionReveal;
